// src/pages/Customers.js
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';

// Change this URL to point to your backend PHP files
const API_URL = "https://htm.bayex.lk/api";

const Customers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [customers, setCustomers] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [customerData, setCustomerData] = useState({
    id: null,
    name: '',
    email: '',
    phone: '',
  });

  // Fetch customers from the API
  const fetchCustomers = () => {
    fetch(`${API_URL}/get_customers.php`)
      .then((res) => res.json())
      .then((data) => {
        setCustomers(data);
      })
      .catch((error) => {
        console.error('Error fetching customers:', error);
      });
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Open modal for adding or editing customer
  const handleOpenModal = (customer = null) => {
    if (customer) {
      setCustomerData(customer);
      setIsEditMode(true);
    } else {
      setCustomerData({ id: null, name: '', email: '', phone: '' });
      setIsEditMode(false);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setCustomerData({ id: null, name: '', email: '', phone: '' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerData({ ...customerData, [name]: value });
  };

  // Save customer via add or update endpoint
  const handleSave = () => {
    const url = isEditMode
      ? `${API_URL}/update_customer.php`
      : `${API_URL}/add_customer.php`;

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(customerData),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 'success') {
          fetchCustomers();
          handleCloseModal();
        } else {
          console.error('Error saving customer:', data.message);
        }
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  };

  // Delete customer after confirmation
  const handleDelete = (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;

    fetch(`${API_URL}/delete_customer.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 'success') {
          fetchCustomers();
        } else {
          console.error('Error deleting customer:', data.message);
        }
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Customers
      </Typography>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={() => handleOpenModal()}
      >
        Add Customer
      </Button>

      {/* Conditional rendering of card vs. table view */}
      {isMobile ? (
        // Mobile: Render customers as a stack of cards.
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {customers.length > 0 ? (
            customers.map((cust) => (
              <Card key={cust.id} variant="outlined">
                <CardContent>
                  <Typography variant="h6">{cust.name}</Typography>
                  <Typography variant="body2"><strong>Email:</strong> {cust.email}</Typography>
                  <Typography variant="body2"><strong>Phone:</strong> {cust.phone}</Typography>
                </CardContent>
                <CardActions>
                  <IconButton onClick={() => handleOpenModal(cust)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(cust.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            ))
          ) : (
            <Typography align="center" variant="body1">
              No customers found.
            </Typography>
          )}
        </Box>
      ) : (
        // Desktop: Render customers in table view.
        <Paper sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customers.map((cust) => (
                <TableRow key={cust.id}>
                  <TableCell>{cust.name}</TableCell>
                  <TableCell>{cust.email}</TableCell>
                  <TableCell>{cust.phone}</TableCell>
                  <TableCell align="center">
                    <IconButton onClick={() => handleOpenModal(cust)} color="primary">
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(cust.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {customers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No customers found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Add/Edit Customer Modal */}
      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle>
          {isEditMode ? "Edit Customer" : "Add New Customer"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            name="name"
            fullWidth
            variant="standard"
            value={customerData.name}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            label="Email"
            name="email"
            fullWidth
            variant="standard"
            value={customerData.email}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            label="Phone"
            name="phone"
            fullWidth
            variant="standard"
            value={customerData.phone}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Customers;
